import React, { useState } from 'react';
import { CheckCircle, XCircle, AlertCircle, Eye, EyeOff } from 'lucide-react';

const ScoreSummaryTable = ({ pageResults }) => {
  const [showSolutions, setShowSolutions] = useState(false);

  // Flatten all annotations across pages
  const allAnnotations = (pageResults || []).flatMap(pr =>
    (pr.annotations || []).map(ann => ({
      ...ann,
      pageNumber: pr.pageNumber
    }))
  );

  const hasSolutions = allAnnotations.some(ann => ann.solution);

  if (allAnnotations.length === 0) {
    return (
      <div className="bg-white rounded-lg border border-gray-200 p-4 text-center text-sm text-gray-500">
        No detailed annotations available.
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
      <div className="px-4 py-3 border-b border-gray-200 flex items-center justify-between">
        <h3 className="text-sm font-semibold text-gray-900">Question Breakdown</h3>
        {hasSolutions && (
          <button
            onClick={() => setShowSolutions(s => !s)}
            className="inline-flex items-center px-2.5 py-1.5 text-xs font-medium rounded-md border border-gray-300 bg-white text-gray-700 hover:bg-gray-50"
          >
            {showSolutions ? <EyeOff className="w-3.5 h-3.5 mr-1" /> : <Eye className="w-3.5 h-3.5 mr-1" />}
            {showSolutions ? 'Hide Solutions' : 'Show Solutions'}
          </button>
        )}
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Question</th>
              <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Result</th>
              <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Marks</th>
              <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Page</th>
              <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Comment</th>
              {showSolutions && (
                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Solution</th>
              )}
              <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Confidence</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {allAnnotations.map((ann, idx) => (
              <tr key={idx} className="hover:bg-gray-50">
                <td className="px-4 py-2 font-medium text-gray-900">
                  {ann.questionLabel || '-'}
                </td>
                <td className="px-4 py-2">
                  {ann.type === 'tick' && <CheckCircle className="w-4 h-4 text-green-500" />}
                  {ann.type === 'cross' && <XCircle className="w-4 h-4 text-red-500" />}
                  {ann.type === 'half-tick' && <AlertCircle className="w-4 h-4 text-orange-500" />}
                </td>
                <td className="px-4 py-2">
                  <span className={`font-medium ${
                    ann.marksAwarded === ann.marksAvailable ? 'text-green-600' :
                    ann.marksAwarded === 0 ? 'text-red-600' : 'text-orange-600'
                  }`}>
                    {ann.marksAwarded}/{ann.marksAvailable}
                  </span>
                </td>
                <td className="px-4 py-2 text-gray-500">{ann.pageNumber}</td>
                <td className="px-4 py-2 text-gray-600 text-xs max-w-xs truncate">
                  {ann.comment || '-'}
                </td>
                {showSolutions && (
                  <td className="px-4 py-2 text-gray-700 text-xs max-w-sm whitespace-pre-wrap">
                    {ann.solution || '-'}
                  </td>
                )}
                <td className="px-4 py-2">
                  {ann.confidence === 'low' && (
                    <span className="px-1.5 py-0.5 text-xs bg-yellow-100 text-yellow-700 rounded">Low</span>
                  )}
                  {ann.confidence === 'medium' && (
                    <span className="px-1.5 py-0.5 text-xs bg-blue-100 text-blue-700 rounded">Med</span>
                  )}
                  {ann.confidence === 'high' && (
                    <span className="px-1.5 py-0.5 text-xs bg-green-100 text-green-700 rounded">High</span>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default ScoreSummaryTable;
